import React, { useState, useEffect } from "react"; // Import useEffect
import { EuiButtonEmpty, EuiFlexGroup, EuiFlexItem, EuiIcon, EuiCopy } from "@elastic/eui";
import { createUseStyles } from "react-jss";
import Popover from "./Popover";

export const useIconPopoverStyles = createUseStyles({
  list: {
    minWidth: "200px",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    "& li": {
      padding: "6px 12px",
      borderBottom: "1px solid var(--border-color)",
    },
  },
  editLink: {
    padding: "6px 12px",
    cursor: "pointer",
    borderRadius: "0px 0px 6px 6px",
    color: "#0071C2",
    "&:hover": {
      backgroundColor: "var(--card-background)",
    },
    background: "var(--card-background)",
  },
  popover: {
    boxShadow: "none",
  },
  valueText: {
    color: "var(--text)",
  },
  tagText: {
    paddingRight: "4px",
  },
  numText: {
    color: "var(--text)",
    fontSize: "var(--fs12)",
    fontWeight: 400,
  },
  copyButton: {
    gap: 0,
  },
});

interface IconPopoverProps {
  icon?: any;
  iconColor?: string;
  items: any[];
  row?: any;
  buttonText?: string;
  onButtonClick?: (row) => void;
  showCount?: boolean;
  textSize?: string;
  copy?: boolean;
}

const IconPopover = ({
  icon,
  iconColor = "#9170BB",
  items,
  row,
  buttonText,
  onButtonClick,
  showCount = true,
  textSize,
  copy = false,
}: IconPopoverProps) => {
  const classes = useIconPopoverStyles();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  if (items.length === 0) return null;

  const handleButtonClick = () => setIsPopoverOpen((prev) => !prev);

  const closePopover = () => {
    setIsPopoverOpen(false);
  };

  useEffect(() => {
    if (!isPopoverOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        closePopover();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isPopoverOpen]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    closePopover();
    if (onButtonClick) {
      onButtonClick(row);
    }
  };

  const button = (
    <EuiButtonEmpty onClick={handleButtonClick}>
      <EuiFlexGroup alignItems="center" gutterSize="none">
        <EuiFlexItem grow={false}>
          {typeof icon === "string" && (
            <EuiIcon
              type={icon}
              style={{ marginRight: showCount ? "5px" : "0px" }}
              size="s"
              color={iconColor}
            />
          )}
          {typeof icon !== "string" && (
            <span style={{ marginRight: showCount ? "5px" : "0px" }}>{icon}</span>
          )}
        </EuiFlexItem>
        {showCount && (
          <EuiFlexItem grow={false}>
            <span className={classes.numText}>{items.length}</span>
          </EuiFlexItem>
        )}
      </EuiFlexGroup>
    </EuiButtonEmpty>
  );

  return (
    <Popover
      className={classes.popover}
      button={button}
      isOpen={isPopoverOpen}
      onClose={closePopover}
      panelPaddingSize="none"
      children={
        <ul className={classes.list}>
          {items.map((i, idx) => (
            <li key={`${i.value}-${idx}`}>
              <EuiFlexGroup alignItems="center" gutterSize="none">
                <EuiFlexItem grow={false}>
                  {typeof icon === "string" && (
                    <EuiIcon
                      type={icon}
                      style={{ marginRight: "5px" }}
                      size="s"
                      color={iconColor}
                    />
                  )}
                  {typeof icon !== "string" && <span style={{ marginRight: "5px" }}>{icon}</span>}
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <span
                    className={classes.valueText}
                    style={textSize ? { fontSize: textSize } : undefined}>
                    {icon === "tag" && <span className={classes.tagText}>{i.name}:</span>}
                    {i.value}
                  </span>
                </EuiFlexItem>
                {copy && (
                  <EuiFlexItem grow={false}>
                    <EuiCopy textToCopy={i.value}>
                      {(copyFn) => (
                        <EuiButtonEmpty
                          className={classes.copyButton}
                          iconType="copy"
                          flush="right"
                          color="text"
                          onClick={copyFn}
                          size="s"
                          aria-label="Copy value"
                        />
                      )}
                    </EuiCopy>
                  </EuiFlexItem>
                )}
              </EuiFlexGroup>
            </li>
          ))}
          {buttonText && onButtonClick && (
            <div className={classes.editLink} onClick={handleClick}>
              {buttonText}
            </div>
          )}
        </ul>
      }
    />
  );
};

export default IconPopover;
